import { TileArgs } from 'react-calendar';
import { QuantityInput } from '../../../components/Form/QuantityInput';
import { Select } from '../../../components/Select/Select';
import { useConfig } from '../../../config';
import { Updater } from '../../../hooks/useImmer';
import { useTranslation } from '../../../i18n/i18n';
import { ApiSchedule, RecurrencePattern } from '../../../types';
import { Value, isDate } from '../../Calendar/Calendar';
import { CalendarSelect } from '../../CalendarSelect/CalendarSelect';
import { DEFAULT_SHIP_IN_HOURS, formatCalendarDate, formatScheduleText, isSwedishHoliday, isWeekend } from '../utils';
import { MonthPatternForm } from './MonthPatternForm';
import { WeekPatternForm } from './WeekPatternForm';

interface EditScheduleFormProps {
	schedule: ApiSchedule;
	setSchedule: Updater<ApiSchedule>;
}

export const EditScheduleForm = ({ schedule, setSchedule }: EditScheduleFormProps): JSX.Element => {
	const t = useTranslation().subscriptions;
	const { locale } = useConfig();
	const scheduleText = schedule && formatScheduleText(schedule, locale, t);

	const checkDisabled = ({ date, view }: TileArgs): boolean => {
		//TODO: Make method calculate changing dates of easter using gauss algorithm
		const threeDaysAhead = new Date(new Date().setHours(new Date().getHours() + DEFAULT_SHIP_IN_HOURS));

		return view === 'month' && (isWeekend(date) || isSwedishHoliday(date) || threeDaysAhead > date);
	};

	const onChangeEndDate = (value: Value): void =>
		setSchedule((draft) => void (draft.endDate = value && isDate(value) ? formatCalendarDate(value) : undefined));

	const onChangeStartDate = (value: Value): void =>
		value && isDate(value) ? setSchedule((draft) => void (draft.startDate = formatCalendarDate(value))) : void null;

	return (
		<>
			<div className="form-row">
				<span>{t.schedule.recurrenceValue}:</span>
				<QuantityInput
					iconSize={19}
					defaultValue={schedule?.recurrenceValue || 1}
					step={1}
					onBlur={(e) => setSchedule((draft: ApiSchedule) => void (draft.recurrenceValue = e))}
				/>
				<Select
					large
					placeholder={t.schedule.recurrencePattern}
					searchValue={(query: string) => query}
					list={['daily', 'weekly', 'monthly']}
					renderValue={(value: keyof typeof t.schedule.patterns) => t.schedule.patterns[value]}
					value={schedule?.recurrencePattern}
					onSelect={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) =>
						setSchedule(
							(draft) =>
								void (draft.recurrencePattern = (e.target as HTMLElement).id as RecurrencePattern)
						)
					}
				/>
			</div>
			{schedule?.recurrencePattern === 'weekly' && (
				<WeekPatternForm schedule={schedule} setSchedule={setSchedule} />
			)}
			{schedule?.recurrencePattern === 'monthly' && (
				<MonthPatternForm schedule={schedule} setSchedule={setSchedule} />
			)}
			<CalendarSelect
				label={t.schedule.begins}
				minDate={new Date()}
				onChangeDate={onChangeStartDate}
				dateValue={schedule?.startDate}
				checkDisabled={checkDisabled}
				todayLabel={t.schedule.today}
			/>
			<CalendarSelect
				label={t.schedule.endsOptional}
				minDate={new Date(schedule?.startDate)}
				onChangeDate={onChangeEndDate}
				dateValue={schedule?.endDate}
				todayLabel={t.schedule.today}
				nullable
			/>
			<p className="lead schedule-text">{scheduleText}</p>
		</>
	);
};
